import * as THREE from 'three';
import { HUD, TRAIL_MARKERS } from '../config/tuning';
import { Pool } from '../core/Pool';
import { NULL_ENTITY, type EntityId, type Registry } from '../core/Registry';
import type { Checksum, Checksummable } from '../core/Checksum';

/**
 * Pooled breadcrumb trail along the flight path.
 *
 * This is a debug instrument rather than an effect. Seeing your own track from inside a hard
 * break is the fastest way to judge turn rate, turn radius, and whether the aircraft is
 * slipping — all of which are otherwise invisible from a nose camera. It is also the M1
 * consumer of Pool and Registry, so the overlay's high-water mark and entity count stay real.
 *
 * Nothing here allocates after construction: the meshes come from the pool factory, the active
 * list is a pre-sized array with swap-removal, and the per-frame update writes into existing
 * objects. This is the shape every transient effect takes from M7 on.
 *
 * Positions are metres, ages seconds.
 */

interface Marker {
  mesh: THREE.Mesh;
  /** Seconds since spawn. */
  ageS: number;
  /** Registry handle, so the live entity count in the overlay is real. */
  entity: EntityId;
}

export class TrailMarkers implements Checksummable {
  readonly object3d = new THREE.Group();

  private readonly registry: Registry;
  private readonly pool: Pool<Marker>;
  private readonly active: Marker[];
  private activeCount = 0;
  private ticksSinceSpawn = 0;

  private readonly geometry: THREE.OctahedronGeometry;
  private readonly material: THREE.MeshBasicMaterial;

  constructor(registry: Registry) {
    this.registry = registry;

    this.geometry = new THREE.OctahedronGeometry(TRAIL_MARKERS.sizeM);
    this.material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(HUD.colour),
      transparent: true,
      opacity: HUD.opacity * 0.7,
      depthWrite: false,
    });

    this.pool = new Pool<Marker>(
      'trail',
      () => {
        const mesh = new THREE.Mesh(this.geometry, this.material);
        mesh.visible = false;
        mesh.frustumCulled = true;
        this.object3d.add(mesh);
        return { mesh, ageS: 0, entity: NULL_ENTITY };
      },
      (marker) => {
        marker.ageS = 0;
        marker.mesh.visible = false;
        this.registry.destroy(marker.entity);
        marker.entity = NULL_ENTITY;
      },
      TRAIL_MARKERS.poolCapacity,
    );

    this.active = new Array<Marker>(TRAIL_MARKERS.poolCapacity);
  }

  /** Live marker count. */
  get count(): number {
    return this.activeCount;
  }

  /**
   * Advance one fixed tick, dropping a breadcrumb at `positionM` on the spawn interval.
   * Allocation-free.
   */
  tick(dtS: number, positionM: THREE.Vector3): void {
    for (let i = this.activeCount - 1; i >= 0; i -= 1) {
      const marker = this.active[i];
      marker.ageS += dtS;
      if (marker.ageS >= TRAIL_MARKERS.lifetimeS) {
        this.pool.release(marker);
        this.activeCount -= 1;
        this.active[i] = this.active[this.activeCount];
      }
    }

    this.ticksSinceSpawn += 1;
    if (this.ticksSinceSpawn < TRAIL_MARKERS.spawnIntervalTicks) return;
    this.ticksSinceSpawn = 0;
    if (this.activeCount >= TRAIL_MARKERS.poolCapacity) return;

    const marker = this.pool.acquire();
    marker.ageS = 0;
    marker.entity = this.registry.create();
    marker.mesh.visible = true;
    marker.mesh.position.copy(positionM);
    this.active[this.activeCount] = marker;
    this.activeCount += 1;
  }

  /**
   * Pose markers for a frame. alpha ∈ [0,1) between the last two ticks; dtS is the tick length,
   * so the interpolated age is age + alpha·dt — the same treatment the aircraft's motion gets.
   */
  interpolate(alpha: number, dtS: number): void {
    const lifetime = TRAIL_MARKERS.lifetimeS;
    for (let i = 0; i < this.activeCount; i += 1) {
      const marker = this.active[i];
      const ageS = marker.ageS + alpha * dtS;
      const remaining = 1 - ageS / lifetime;
      marker.mesh.scale.setScalar(remaining > 0 ? remaining : 0);
    }
  }

  /** Marker ages are simulation state, so they take part in the determinism checksum. */
  checksum(into: Checksum): void {
    into.addInt(this.activeCount);
    into.addInt(this.ticksSinceSpawn);
    for (let i = 0; i < this.activeCount; i += 1) {
      into.addFloat(this.active[i].ageS);
    }
  }

  /** Retire every marker and return to the initial state. */
  reset(): void {
    for (let i = this.activeCount - 1; i >= 0; i -= 1) {
      this.pool.release(this.active[i]);
    }
    this.activeCount = 0;
    this.ticksSinceSpawn = 0;
    this.pool.resetHighWaterMark();
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
